import { useCallback, useEffect, useState } from 'react';
import type { ILoadFailure } from '@/services/loadFailure';

/**
 * RD-33 (I-1077, `docs/concept-mobile-app.md`): how long a list may stay on its skeleton before it
 * says so, with a retry. One number and one rule for every list screen (Items, Decisions, Agents,
 * Tasks), so no screen has a timer of its own and none can be left without one.
 *
 * It is the screen's deadline, not the request's: a read the browser holds without ending (WebKit
 * holds a request its page intercepts) never reaches the transport's own timeout, and the screen
 * still has to say so.
 */
export const LIST_LOAD_DEADLINE_MS = 10_000;

/** `true` once `waiting` has held for `ms` under one `key`; any change of either starts again. */
export function useLoadDeadline(
  waiting: boolean,
  key: string,
  ms: number = LIST_LOAD_DEADLINE_MS,
): boolean {
  const [over, setOver] = useState<string | null>(null);
  useEffect(() => {
    // A deadline over for one read is not over for the next (RD-35).
    if (!waiting) {
      setOver(null);
      return;
    }
    const timer = window.setTimeout(() => setOver(key), ms);
    return () => window.clearTimeout(timer);
  }, [waiting, key, ms]);
  return waiting && over === key;
}

/** The failure a screen shows, in the shared banner, when its load passed the deadline. */
export const STALLED_LOAD_FAILURE: ILoadFailure = {
  category: 'offline',
  permission: null,
  stalled: true,
};

/**
 * A list screen's whole use of the rule: whether its first load is `waiting` (nothing known, no
 * failure), under a `key` for what it loads. Past the deadline `failure` is set, for the shared
 * banner; `retry` starts the deadline again and then reads again.
 */
export function useStalledLoad(
  waiting: boolean,
  key: string,
  reload: () => void,
): { failure: ILoadFailure | null; retry: () => void } {
  const [attempt, setAttempt] = useState(0);
  const stalled = useLoadDeadline(waiting, `${key}:${attempt}`);
  const retry = useCallback(() => {
    setAttempt((count) => count + 1);
    reload();
  }, [reload]);
  return { failure: stalled ? STALLED_LOAD_FAILURE : null, retry };
}
